import { useMemo } from 'react';

import { useAppState } from '../../../AppStateProvider';
import type { Contact } from '../../../domain/contact';
import { hasDataToReveal } from '../../../domain/contact';

export type BulkSelectionState = {
  /** All contacts that can potentially be revealed (have data to reveal and no prior reveal errors) */
  contactsAvailableForReveal: Partial<Contact>[];
  /** Selected contacts that can be revealed */
  selectedContactsAvailableForReveal: Partial<Contact>[];
  /** Count of selected contacts that still need to be revealed (not yet revealed) */
  contactsToRevealCount: number;
  /** True if no contacts are selected */
  areNoContactsSelected: boolean;
  /** True if all available contacts are selected */
  areAllSelected: boolean;
};

export const useBulkSelectionState = (contacts: Partial<Contact>[]): BulkSelectionState => {
  const { selectedContacts, notRevealedContactsIds } = useAppState();

  const isContactAvailableForReveal = (contact: Partial<Contact>): boolean => {
    const isRevealError = notRevealedContactsIds.includes(contact.id ?? '');
    const isRevealAvailable = hasDataToReveal(contact);
    return isRevealAvailable && !isRevealError;
  };

  const isContactAvailableForExport = (contact: Partial<Contact>): boolean => {
    return hasDataToReveal(contact);
  };

  const contactsAvailableForReveal = useMemo(
    () => contacts.filter((contact) => isContactAvailableForReveal(contact) && isContactAvailableForExport(contact)),
    [contacts, notRevealedContactsIds]
  );

  const selectedContactsAvailableForReveal = useMemo(
    () =>
      selectedContacts.filter(
        (contact) => isContactAvailableForReveal(contact) && isContactAvailableForExport(contact)
      ),
    [selectedContacts, notRevealedContactsIds]
  );

  const areNoContactsSelected = selectedContactsAvailableForReveal.length === 0;
  const areAllSelected =
    selectedContactsAvailableForReveal.length === contactsAvailableForReveal.length &&
    contactsAvailableForReveal.length > 0;

  const contactsToRevealCount = selectedContactsAvailableForReveal.filter((contact) => !contact.isRevealed).length;

  return {
    contactsAvailableForReveal,
    selectedContactsAvailableForReveal,
    contactsToRevealCount,
    areNoContactsSelected,
    areAllSelected,
  };
};
